import { Actions, KeyMaps, Options, Tool } from "./types";

const DEFAULT_OPTIONS: Options = {
  theme: "mirrormark",
  tabSize: "2",
  indentWithTabs: true,
  lineWrapping: true,
  extraKeys: {
    Enter: "newlineAndIndentContinueMarkdownList",
  },
  mode: "markdown",
};

export function mirrorMark(options: Partial<Options> = {}) {
  return new MirrorMark({
    ...DEFAULT_OPTIONS,
    ...options,
  });
}

class MirrorMark {
  private cm?: any;
  private options: Options;

  constructor(options: Options) {
    this.options = options;
  }

  render = (codeMirror: any) => {
    if (codeMirror.mirrorMarkedAdded) {
      return;
    }

    this.registerKeyMaps(this.keyMaps);
    this.cm = codeMirror;
    const toolbar = this.setToolbar(this.tools);
    codeMirror.mirrorMarkAdded = true;
    return toolbar;
  };

  /**
   * Default Tools in Toolbar
   * @todo - update so it's not so tightly coupled with Font Awesome.
   */
  tools: Tool[] = [
    { name: "bold", action: "bold", className: "fas fa-bold" },
    { name: "italicize", action: "italicize", className: "fas fa-italic" },
    {
      name: "blockquote",
      action: "blockquote",
      className: "fas fa-quote-left",
    },
    { name: "link", action: "link", className: "fas fa-link" },
    { name: "image", action: "image", className: "fas fa-image" },
    {
      name: "unorderedList",
      action: "unorderedList",
      className: "fas fa-list",
    },
    { name: "orderedList", action: "orderedList", className: "fas fa-list-ol" },
    { name: "fullScreen", action: "fullScreen", className: "fas fa-expand" },
    {
      name: "heading",
      action: "h1",
      className: "fas fa-heading",
    },
  ];

  /**
   * Default Keymaps
   * @type {Object}
   */
  keyMaps: KeyMaps = {
    "Cmd-B": "bold",
    "Cmd-I": "italicize",
    "Cmd-'": "blockquote",
    "Cmd-Alt-L": "orderedList",
    "Cmd-L": "unorderedList",
    "Cmd-Alt-I": "image",
    "Cmd-H": "hr",
    "Cmd-K": "link",
  };

  actions: Actions = {
    bold() {
      this.insertAround("**", "**");
    },
    italicize() {
      this.insertAround("*", "*");
    },
    code() {
      this.insertAround("```\r\n", "\r\n```");
    },
    blockquote() {
      this.insertBefore("> ", 2);
    },
    orderedList() {
      this.insertBefore("1. ", 3);
    },
    unorderedList() {
      this.insertBefore("* ", 2);
    },
    image() {
      this.insertBefore("![](http://)", 2);
    },
    link() {
      this.insertAround("[", "](http://)");
    },
    hr() {
      this.insert("---");
    },
    h1() {
      this.insertBefore("# ", 2);
    },
    fullScreen() {
      const el = this.cm.getWrapperElement();

      // https://developer.mozilla.org/en-US/docs/DOM/Using_fullscreen_mode
      const doc = document as any;
      const isFull =
        doc.fullScreen || doc.mozFullScreen || doc.webkitFullScreen;
      const request = () => {
        if (el.requestFullscreen) {
          el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          el.webkitRequestFullscreen();
        } else if (el.mozRequestFullScreen) {
          el.mozRequestFullScreen();
        } else if (el.msRequestFullscreen) {
          el.msRequestFullscreen();
        }
      };
      const cancel = () => {
        if (doc.cancelFullScreen) {
          doc.cancelFullScreen();
        } else if (doc.mozCancelFullScreen) {
          doc.mozCancelFullScreen();
        } else if (doc.webkitCancelFullScreen) {
          doc.webkitCancelFullScreen();
        }
      };
      if (!isFull) {
        request();
      } else if (cancel) {
        cancel();
      }
    },
  };

  /**
   * Setup the toolbar
   */
  setToolbar = (tools: Tool[]) => {
    const toolbar = document.createElement("ul");
    toolbar.className = this.options.theme + "-" + "toolbar";

    tools = this.generateToolList(tools);

    tools.forEach((tool: any) => {
      toolbar.appendChild(tool);
    });

    const cmWrapper = this.cm.getWrapperElement();
    cmWrapper.parentNode.insertBefore(toolbar, cmWrapper);
    return toolbar;
  };

  /**
   * Register Keymaps by extending the extraKeys object
   * @param {Object} keyMaps
   */
  registerKeyMaps = (keyMaps: Record<string, string>) => {
    for (const name of Object.keys(keyMaps)) {
      if (typeof this.actions[keyMaps[name]] !== "function") {
        throw new Error(
          `MirrorMark - '${keyMaps[name]}' is not a registered action`
        );
      }

      this.options.extraKeys = {
        ...this.options.extraKeys,
        [name]: this.actions[keyMaps[name]].bind(this),
      };
    }
  };

  /**
   * Register actions by extending the default actions
   * @param  {Object} actions [description]
   */
  registerActions = (actions: Record<string, any>) => ({
    ...this.actions,
    ...actions,
  });

  /**
   * Register tools by extending and overwriting the default tools
   * @param  {Array} tools
   * @param  {Bool} replace - replace the default tools with the ones provided. Defaults to false.
   */
  registerTools = (tools: Tool[], replace?: boolean) => {
    for (const action in tools) {
      if (
        this.actions[tools[action].action] &&
        typeof this.actions[tools[action].action] !== "function"
      ) {
        throw new Error(
          `MirrorMark - '${tools[action].action}' is not a registered action`
        );
      }
    }

    if (replace) {
      this.tools = tools;
      return;
    }

    this.tools = this.tools.concat(tools);
  };

  /**
   * A recursive function to generate and return an unordered list of tools
   */
  generateToolList = (tools: Tool[]): Tool[] => {
    return tools.map((tool: any) => {
      const item = document.createElement("li") as any;
      const anchor = document.createElement("a");

      item.className = tool.name;

      if (tool.className) {
        anchor.className = tool.className;
      }

      if (tool.showName) {
        const text = document.createTextNode(tool.name);
        anchor.appendChild(text);
      }

      if (tool.action) {
        anchor.onclick = () => {
          this.cm.focus();
          this.actions[tool.action].call(this);
        };
      }

      item.appendChild(anchor);

      if (tool.nested) {
        item.className += " has-nested";
        const ul = document.createElement("ul");
        ul.className = this.options.theme + "-toolbar-list";
        const nested = this.generateToolList(tool.nested);
        nested.forEach((nestedItem: any) => {
          ul.appendChild(nestedItem);
        });

        item.appendChild(ul);
      }

      return item;
    });
  };

  insert = (insertion: string) => {
    const doc = this.cm.getDoc();
    const cursor = doc.getCursor();

    doc.replaceRange(insertion, { line: cursor.line, ch: cursor.ch });
  };

  insertAround = (start: string, end: string) => {
    const doc = this.cm.getDoc();
    const cursor = doc.getCursor();

    if (doc.somethingSelected()) {
      const selection = doc.getSelection();
      doc.replaceSelection(start + selection + end);
    } else {
      // If no selection then insert start and end args and set cursor position between the two.
      doc.replaceRange(start + end, { line: cursor.line, ch: cursor.ch });
      doc.setCursor({ line: cursor.line, ch: cursor.ch + start.length });
    }
  };

  /**
   * Insert a string before a selection
   * @param  {String} insertion
   */
  insertBefore = (insertion: string, cursorOffset: number) => {
    const doc = this.cm.getDoc();
    const cursor = doc.getCursor();

    if (doc.somethingSelected()) {
      const selections = doc.listSelections();
      selections.forEach((selection: any) => {
        const pos = [selection.head.line, selection.anchor.line].sort();

        for (let i = pos[0]; i <= pos[1]; i++) {
          doc.replaceRange(insertion, { line: i, ch: 0 });
        }

        doc.setCursor({ line: pos[0], ch: cursorOffset || 0 });
      });
    } else {
      doc.replaceRange(insertion, { line: cursor.line, ch: 0 });
      doc.setCursor({ line: cursor.line, ch: cursorOffset || 0 });
    }
  };
}
